import { Injectable } from '@angular/core';

/**
 * Partitioned token storage for the three Branduals SPAs.
 *
 * The OIDC library serializes its ENTIRE per-config state into ONE configId-keyed JSON
 * blob and never passes individual keys — so the partition is blob-FIELD splitting:
 * write() parses the blob and routes each field to its classified home, read() recomposes.
 *
 * The classification is TOTAL and deliberate:
 *  - TOKEN fields (the raw tokens, their expiry, token-derived user data, and the latent
 *    reusable refresh token) live in MEMORY only — nothing credential-bearing survives the
 *    tab. They are shared across same-origin tabs over a BroadcastChannel, and a cold boot
 *    ASKS peers once (awaited pre-bootstrap, bounded ~50 ms) so a second tab signs in
 *    without a round-trip.
 *  - FLOW-CONTROL fields (PKCE verifier, CSRF state, nonce) live in tab-local
 *    sessionStorage: one-shot non-credential values that must survive the sign-in
 *    redirect, and per-tab by construction so two concurrent sign-ins cannot consume each
 *    other's verifier.
 *  - session_state lives in sessionStorage AND is shared ONLY on the cold-boot ask/answer
 *    leg — never pushed on live writes. The check-session iframe re-reads it on every
 *    poll, so a live push would hand a stale tab the NEW value and suppress exactly the
 *    changed-identifier detection the poll exists for; the cold-boot copy is what lets a
 *    peer-hydrated tab (which never authorizes) keep session monitoring at all.
 *  - An UNKNOWN key routes to MEMORY plus a console.warn naming it: an unknown
 *    flow-control key then breaks loudly at the next sign-in (visible, recoverable),
 *    while an unknown token key can never silently gain durability. sessionStorage is
 *    exactly the durability class tokens must not reach, so it is never the default.
 *  - This service NEVER writes localStorage; the legacy blob there is purge-only
 *    (the pre-bootstrap cleaner's job).
 */

const CHANNEL_NAME = 'branduals-auth-storage';
const SESSION_PREFIX = 'branduals-auth.';
const HYDRATE_TIMEOUT_MS = 50;

/** Memory-homed, channel-shared token fields. */
const MEMORY_FIELDS = new Set([
  'authnResult',
  // The raw access token rides its OWN blob field, written on every callback and renew;
  // the library's authenticated check reads it, so a peer-hydrated tab needs the share.
  'authzData',
  'access_token_expires_at',
  'userData',
  // Latent today (unsafe refresh-token reuse is unset in all three apps) — classified so
  // a config change can never grant it durability.
  'reusable_refresh_token',
]);

/** Tab-local sessionStorage fields (plus every `storage*` flow flag by prefix). */
const SESSION_FIELDS = new Set([
  'codeVerifier',
  'authStateControl',
  'authNonce',
  'session_state',
  'authWellKnownEndPoints',
  'jwtKeys',
  'redirect',
  'popupauth',
]);

function isSessionField(key: string): boolean {
  return SESSION_FIELDS.has(key) || key.startsWith('storage');
}

type Fields = Record<string, unknown>;

interface AskMessage { type: 'ask'; }
interface AnswerMessage {
  type: 'answer';
  configs: Record<string, { memory: Fields; sessionState?: unknown }>;
}
interface WriteMessage { type: 'write'; configId: string; memory: Fields; }
interface ClearMessage { type: 'clear'; configId?: string; }
type ChannelMessage = AskMessage | AnswerMessage | WriteMessage | ClearMessage;

/** The BroadcastChannel surface the context needs — narrow, so tests can fake the bus. */
export interface ChannelLike {
  postMessage(message: ChannelMessage): void;
  addEventListener(type: 'message', listener: (event: { data: ChannelMessage }) => void): void;
}

/**
 * One tab's storage state: the memory token partition, the sessionStorage handle, and the
 * channel. Module-level (not DI) because hydration must complete BEFORE
 * bootstrapApplication — no injector exists yet; the DI service wraps the same context.
 * Tests construct explicit contexts to simulate tabs.
 */
export class AuthStorageContext {
  private readonly memory = new Map<string, Fields>();
  // Configs whose memory partition this tab has ever actually held (own sign-in, peer
  // hydration, or a peer push). Only such a tab may broadcast an EMPTY memory partition:
  // for it, empty is a real de-authentication to propagate — while a cold tab that never
  // held tokens (its 50 ms hydration ask went unanswered, then the library wrote a
  // flow-only blob like the discovery cache) must not wipe a signed-in sibling's tokens
  // with its first write.
  private readonly hasHeldMemory = new Set<string>();
  private readonly session: Storage | null;
  private readonly channel: ChannelLike | null;

  constructor(session: Storage | null, channel: ChannelLike | null) {
    this.session = session;
    this.channel = channel;
    this.channel?.addEventListener('message', (event) => this.onMessage(event.data));
  }

  read(configId: string): string | null {
    const sessionRaw = this.session?.getItem(SESSION_PREFIX + configId);
    const sessionFields: Fields = sessionRaw ? JSON.parse(sessionRaw) : {};
    const memoryFields = this.memory.get(configId) ?? {};
    const merged = { ...sessionFields, ...memoryFields };
    return Object.keys(merged).length === 0 ? null : JSON.stringify(merged);
  }

  write(configId: string, value: string): void {
    let parsed: Fields;
    try {
      parsed = JSON.parse(value) as Fields;
    } catch {
      // A non-JSON value has no fields to classify — memory keeps it non-durable.
      console.warn(`[auth-storage] non-JSON blob for config '${configId}' held in memory`);
      this.memory.set(configId, { __raw: value });
      return;
    }

    const memoryFields: Fields = {};
    const sessionFields: Fields = {};
    for (const [key, fieldValue] of Object.entries(parsed)) {
      if (MEMORY_FIELDS.has(key)) {
        memoryFields[key] = fieldValue;
      } else if (isSessionField(key)) {
        sessionFields[key] = fieldValue;
      } else {
        // Fail-visible: an unclassified key must never silently gain durability.
        console.warn(`[auth-storage] unclassified storage key '${key}' held in memory`);
        memoryFields[key] = fieldValue;
      }
    }

    // The library writes its complete state each time — both partitions replace wholesale.
    this.memory.set(configId, memoryFields);
    this.session?.setItem(SESSION_PREFIX + configId, JSON.stringify(sessionFields));

    // Live pushes carry MEMORY fields only — session_state deliberately never rides them.
    // An empty partition is pushed only by a tab that has actually HELD tokens for this
    // config (a real de-authentication); a never-authenticated tab's flow-only writes are
    // not authoritative peer state and must not erase a signed-in sibling's tokens.
    const holdsMemory = Object.keys(memoryFields).length > 0;
    if (holdsMemory) this.hasHeldMemory.add(configId);
    if (holdsMemory || this.hasHeldMemory.has(configId)) {
      this.channel?.postMessage({ type: 'write', configId, memory: memoryFields });
    }
  }

  remove(configId: string): void {
    this.memory.delete(configId);
    this.hasHeldMemory.delete(configId);
    this.session?.removeItem(SESSION_PREFIX + configId);
    this.channel?.postMessage({ type: 'clear', configId });
  }

  clear(): void {
    this.memory.clear();
    this.hasHeldMemory.clear();
    if (this.session) {
      const doomed: string[] = [];
      for (let i = 0; i < this.session.length; i++) {
        const key = this.session.key(i);
        if (key?.startsWith(SESSION_PREFIX)) doomed.push(key);
      }
      doomed.forEach((key) => this.session!.removeItem(key));
    }
    this.channel?.postMessage({ type: 'clear' });
  }

  /**
   * Cold-boot peer ask: posts once and waits for the FIRST answer, bounded so an
   * answerless (genuinely cold) boot never stalls bootstrap. Merges peer memory fields
   * and adopts the peer's session_state only when this tab has none of its own.
   */
  hydrateFromPeers(): Promise<void> {
    if (!this.channel) return Promise.resolve();

    return new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (!settled) {
          settled = true;
          resolve();
        }
      };

      this.channel!.addEventListener('message', (event) => {
        if (settled || event.data.type !== 'answer') return;
        for (const [configId, state] of Object.entries(event.data.configs)) {
          this.memory.set(configId, state.memory);
          if (Object.keys(state.memory).length > 0) this.hasHeldMemory.add(configId);
          if (state.sessionState !== undefined && this.session) {
            const key = SESSION_PREFIX + configId;
            const ownRaw = this.session.getItem(key);
            const own: Fields = ownRaw ? JSON.parse(ownRaw) : {};
            if (own['session_state'] === undefined) {
              own['session_state'] = state.sessionState;
              this.session.setItem(key, JSON.stringify(own));
            }
          }
        }
        finish();
      });

      this.channel!.postMessage({ type: 'ask' });
      setTimeout(finish, HYDRATE_TIMEOUT_MS);
    });
  }

  private onMessage(message: ChannelMessage): void {
    switch (message.type) {
      case 'write':
        this.memory.set(message.configId, message.memory);
        if (Object.keys(message.memory).length > 0) this.hasHeldMemory.add(message.configId);
        break;
      case 'clear':
        // A peer's clear kills THIS tab's tokens too (sign-out reaches every tab) but
        // never this tab's own sessionStorage flow state — a peer mid-sign-in keeps its
        // PKCE verifier and finishes or fails on its own terms.
        if (message.configId !== undefined) {
          this.memory.delete(message.configId);
        } else {
          this.memory.clear();
        }
        break;
      case 'ask': {
        const configs: AnswerMessage['configs'] = {};
        for (const [configId, memoryFields] of this.memory.entries()) {
          const sessionRaw = this.session?.getItem(SESSION_PREFIX + configId);
          const sessionFields: Fields = sessionRaw ? JSON.parse(sessionRaw) : {};
          configs[configId] = {
            memory: memoryFields,
            // The one sanctioned session_state share: the cold-boot answer, so a
            // peer-hydrated tab (which never authorizes) can keep session monitoring.
            ...(sessionFields['session_state'] !== undefined
              ? { sessionState: sessionFields['session_state'] }
              : {}),
          };
        }
        if (Object.keys(configs).length > 0) {
          this.channel?.postMessage({ type: 'answer', configs });
        }
        break;
      }
      case 'answer':
        // Handled by the hydrateFromPeers listener; steady-state tabs ignore answers.
        break;
    }
  }
}

let defaultContext: AuthStorageContext | null = null;

function getDefaultContext(): AuthStorageContext {
  if (!defaultContext) {
    const session = typeof sessionStorage !== 'undefined' ? sessionStorage : null;
    const channel = typeof BroadcastChannel !== 'undefined'
      ? (new BroadcastChannel(CHANNEL_NAME) as unknown as ChannelLike)
      : null;
    defaultContext = new AuthStorageContext(session, channel);
  }
  return defaultContext;
}

/**
 * Awaited from each app's main.ts BEFORE bootstrapApplication: the library's storage read
 * is synchronous, so peer hydration must complete inside the pre-bootstrap window.
 * Anonymous cold loads pay the ~50 ms bound on every entry — accepted and stated.
 */
export function hydrateFromPeers(): Promise<void> {
  return getDefaultContext().hydrateFromPeers();
}

/**
 * The pre-partition localStorage token blob is purged UNCONDITIONALLY on every boot:
 * tokens live in the memory+sessionStorage partition now, so anything under the legacy
 * keys is durable debris — and the first new-code load deletes it for the whole origin.
 * Lives in the package (one implementation, testable) and is called from each app's
 * main.ts inside the pre-bootstrap window, before the library's synchronous storage read.
 */
export function purgeLegacyTokenBlob(storage: Storage = localStorage): void {
  const doomed: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key && (key.startsWith('0-') || key.startsWith('oidc.') || key.startsWith('authnResult'))) {
      doomed.push(key);
    }
  }
  doomed.forEach(key => storage.removeItem(key));
}

/** The DI face over the module-level context — what the apps provide as AbstractSecurityStorage. */
@Injectable({ providedIn: 'root' })
export class BrandualsAuthStorageService {
  read(key: string): string | null {
    return getDefaultContext().read(key);
  }

  write(key: string, value: string): void {
    getDefaultContext().write(key, value);
  }

  remove(key: string): void {
    getDefaultContext().remove(key);
  }

  clear(): void {
    getDefaultContext().clear();
  }
}
